/**
 * Tranzila's third-party scripts, loaded only for Tranzila tenants.
 *
 * These three tags used to sit in `public/index.html`, so every payment page served them --
 * a ZCredit or Pelecard tenant shipped jQuery and an Apple Pay integration from
 * direct.tranzila.com that nothing on the page ever called. Two costs: a third party
 * executing script on a payment page for no reason, and this, which was the third tag:
 *
 *   <script>document.write('<script src="...tranzilanapple_v3.js?v=' + Date.now() + '">…')</script>
 *
 * `document.write` after the document has finished parsing replaces the whole document. If
 * that script tag ever evaluated late -- a slow network, a browser that defers differently --
 * it would blank the page a payer was about to type a card number into.
 *
 * Injected after the row loads instead, when `credit_type` says Tranzila.
 */

/** PaymentType::CRADIT_TRANZILA. */
export const TRANZILA_CREDIT_TYPE = 3

export const isTranzila = (creditType) =>
  creditType !== null && creditType !== undefined && creditType !== '' &&
  Number(creditType) === TRANZILA_CREDIT_TYPE

/**
 * jQuery first: `tranzilanapple_v3.js` expects it, and the `noConflict` handoff below only
 * makes sense once both have run.
 *
 * The cache-buster is what the old `document.write` was for -- Tranzila ship changes to this
 * file under the same name -- so it is kept.
 */
export const tranzilaScriptUrls = (now = Date.now()) => [
  'https://direct.tranzila.com/Tranzila_files/jquery.js',
  'https://direct.tranzila.com/js/tranzilanapple_v3.js?v=' + now,
]

let loaded = false

/**
 * Append Tranzila's scripts, in order, once.
 *
 * Sequential rather than parallel: the Apple Pay script reads `jQuery` at evaluation time, so
 * appending both at once is a race that passes on a fast connection.
 *
 * Failure is swallowed on purpose. The gateway's iframe does not depend on any of this -- only
 * the Apple Pay button does -- so a third-party script having a bad day must not take the
 * payment page down with it.
 */
export const loadTranzila = (creditType, doc = document) => {
  if (loaded || !isTranzila(creditType)) {
    return
  }

  loaded = true

  const [jqueryUrl, appleUrl] = tranzilaScriptUrls()

  const append = (src, onload) => {
    const el = doc.createElement('script')
    el.src = src
    el.async = true
    el.onload = onload || null
    el.onerror = () => {}
    doc.head.appendChild(el)
    return el
  }

  append(jqueryUrl, () => {
    append(appleUrl, () => {
      // Keeps Tranzila's jQuery out of the global namespace, as the old inline tag did.
      try {
        if (typeof window !== 'undefined' && window.jQuery) {
          window.$n = window.jQuery.noConflict(true)
        }
      } catch (e) {
        /* noConflict is a courtesy, not a requirement */
      }
    })
  })
}

/** Tests only: the module-level "already loaded" latch has to be resettable. */
export const __resetTranzilaForTests = () => {
  loaded = false
}
